import { useEffect, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, BACKGROUND } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const IDLE_WARN = 2 * 60 * 1000;
const ABS_WARN = 5 * 60 * 1000;
const NUDGE_AFTER = 5 * 60 * 1000;

const inWords = (ms) => (ms > 60000 ? `${Math.ceil(ms / 60000)} Minuten` : "weniger als einer Minute");

function useSessionClock() {
  const [clock, setClock] = useState(null);
  const lastActive = useRef(Date.now());
  useEffect(() => {
    const onClock = (e) => {
      const t = Date.now();
      setClock({ end: t + e.detail.remaining * 1000, idleEnd: t + e.detail.idle * 1000 });
      if (!e.detail.background) lastActive.current = t;
    };
    window.addEventListener("control:session-clock", onClock);
    return () => window.removeEventListener("control:session-clock", onClock);
  }, []);
  useEffect(() => {
    // Real input (key/pointer) after 5 quiet minutes counts as activity; background requests never do.
    const onInput = () => {
      if (!clock || Date.now() - lastActive.current < NUDGE_AFTER) return;
      lastActive.current = Date.now();
      api.post("/auth/activity").catch(() => {});
    };
    window.addEventListener("keydown", onInput);
    window.addEventListener("pointerdown", onInput);
    return () => {
      window.removeEventListener("keydown", onInput);
      window.removeEventListener("pointerdown", onInput);
    };
  }, [clock]);
  return clock;
}

function useNow() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

// Session clock from server headers (no polling): warns before the inactivity and the absolute end; at the end the
// server is asked once in the background, which answers 401 and starts the normal re-login flow.
export function SessionTimer() {
  const { logout } = useAuth();
  const clock = useSessionClock();
  const now = useNow();
  const [absSeen, setAbsSeen] = useState(false);
  const ended = useRef(null);
  const idleLeft = clock ? clock.idleEnd - now : Infinity;
  const absLeft = clock ? clock.end - now : Infinity;
  useEffect(() => {
    if (!clock || Math.min(idleLeft, absLeft) > 0 || ended.current === clock) return;
    ended.current = clock;
    window.dispatchEvent(new Event("control:session-ending"));
    api.get("/auth/me", BACKGROUND).catch(() => {});
  }, [clock, idleLeft, absLeft]);
  if (!clock) return null;
  const idleWarn = idleLeft > 0 && idleLeft <= IDLE_WARN && idleLeft <= absLeft;
  const absWarn = !idleWarn && absLeft > 0 && absLeft <= ABS_WARN && !absSeen;
  const stay = () => api.post("/auth/activity").catch(() => {});
  return (
    <AlertDialog open={idleWarn || absWarn} onOpenChange={(open) => { if (!open) { if (idleWarn) stay(); else setAbsSeen(true); } }}>
      <AlertDialogContent data-testid="session-expiry-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="session-expiry-title">{idleWarn ? "Deine Sitzung endet bald" : "Höchstdauer der Sitzung erreicht"}</AlertDialogTitle>
          <AlertDialogDescription data-testid="session-expiry-text">
            {idleWarn
              ? `Nach 30 Minuten ohne Aktivität endet deine Sitzung in ${inWords(idleLeft)}. Offene Eingaben werden dann in diesem Tab als Entwurf gesichert.`
              : `Jede Sitzung endet spätestens 8 Stunden nach der Anmeldung – diese in ${inWords(absLeft)}. Speichere offene Eingaben jetzt; danach ist eine neue Anmeldung mit 2FA nötig.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {idleWarn ? (
            <>
              <AlertDialogCancel onClick={(e) => { e.preventDefault(); logout(); }} data-testid="session-expiry-logout">Abmelden</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { e.preventDefault(); stay(); }} data-testid="session-expiry-stay">Angemeldet bleiben</AlertDialogAction>
            </>
          ) : (
            <AlertDialogAction onClick={() => setAbsSeen(true)} data-testid="session-expiry-ack">Verstanden</AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
